import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Star,
  CheckCircle2,
  ArrowRight,
  FileText,
  Send,
  Globe2,
  Sparkles,
  Trophy,
  Camera,
} from 'lucide-react';
import { supabase, Category } from '../../lib/supabase';
import { WrappedStats } from '../wrapped/types';
import BasesModal from './BasesModal';
import ReviveSection from './ReviveSection';
import PastEditionsSection from './PastEditionsSection';
import CommitteeJurySection from './CommitteeJurySection';
import StatsBand from './StatsBand';
import ParticipationStatsBand from './ParticipationStatsBand';
import TestimonialsSection from './TestimonialsSection';
import LandingFooter from './LandingFooter';
import ScrollProgress from './ScrollProgress';
import HeroStars from './HeroStars';
import SeoMeta from '../SeoMeta';
import Countdown, { GALA_DATETIME } from '../Countdown';
import './Landing.css';

interface Winner {
  category: string;
  winner: string;
  extra?: string;
}

const WINNERS_2025: Winner[] = [
  { category: 'Persona Innovadora del Año', winner: 'Ylenia Díaz' },
  { category: 'Mujer destacada del Sector Público', winner: 'Natalie González' },
  { category: 'Nuevo talento público', winner: 'Paula Daniela Fernández Lopes' },
  {
    category: 'Valor de las Personas Excelentes',
    winner: 'Javier Peña',
    extra: '(Distinción Madrid Talento)',
  },
  {
    category: 'Creatividad en la innovación',
    winner:
      'Digitaliza Madrid: Impulso a la Innovación Pública a través de la Digitalización, Formación y Co-Creación',
  },
  { category: 'Benchmarking', winner: 'Proyecto Transfer del IAAP' },
  {
    category: 'Proyecto más transformador',
    winner: 'Médico en tu territorio — Departamento de Cundinamarca',
  },
  {
    category: 'Eficiencia en las AA.PP.',
    winner: 'Obras con Capital Social — Gobernación de Cundinamarca',
  },
  { category: 'Transformación Digital', winner: 'Programa Burocracia Cero' },
  {
    category: 'Gestión de personas',
    winner:
      'Carrera Profesional Horizontal: Hacia un Modelo de Gestión Pública Más Innovador, Eficiente y Centrado en las Personas',
  },
  { category: 'Buen Gobierno', winner: 'Oficina de Mediación' },
  { category: 'Categoría Especial', winner: 'Matilde Castellanos' },
];

interface Phase {
  startDate: string; // ISO YYYY-MM-DD — inicio de fase (para resaltar la actual)
  endDate: string;   // ISO YYYY-MM-DD — fin de fase
  dates: string;
  title: string;
  cta: {
    label: string;
    href?: string;
    disabled?: boolean;
    action?: 'open-bases' | 'present-candidatura' | 'view-candidaturas' | 'vote' | 'jury-evaluation' | 'final-two';
  };
}

const PHASES: Phase[] = [
  {
    startDate: '2026-04-27',
    endDate: '2026-05-04',
    dates: '27 abril – 4 mayo',
    title: 'Exposición de bases',
    cta: {
      label: 'Ver bases',
      action: 'open-bases',
    },
  },
  {
    startDate: '2026-05-04',
    endDate: '2026-07-03',
    dates: '4 mayo – 3 julio',
    title: 'Presentación de candidaturas',
    cta: { label: 'Ver candidaturas presentadas', action: 'view-candidaturas' },
  },
  {
    startDate: '2026-07-13',
    endDate: '2026-09-04',
    dates: '13 julio – 4 septiembre',
    title: 'Fase evaluación jurado',
    cta: { label: 'Ver valoración del jurado', action: 'jury-evaluation' },
  },
  {
    startDate: '2026-09-08',
    // La fase de votación pública termina el 22, pero el foco de la línea de
    // tiempo debe pasar ya ese mismo día a "Proclamación de finalistas" —
    // de ahí que endDate quede un día antes de la fecha mostrada (8-22).
    endDate: '2026-09-21',
    dates: '8 – 22 septiembre',
    title: 'Evaluación de la comunidad',
    cta: { label: 'Vota a los finalistas', action: 'vote' },
  },
  {
    startDate: '2026-09-22',
    endDate: '2026-09-22',
    dates: '22 de septiembre',
    title: 'Proclamación de finalistas',
    cta: { label: 'Ver los 2 últimos finalistas', action: 'final-two' },
  },
  {
    startDate: '2026-12-10',
    endDate: '2026-12-10',
    dates: '10 diciembre',
    title: 'Gala entrega premios',
    cta: { label: 'Próximamente', disabled: true },
  },
];

/**
 * Devuelve el índice de la fase "actual" — la primera cuya fecha de cierre
 * aún no ha pasado. Si todas han finalizado, devuelve -1.
 */
const getCurrentPhaseIndex = (phases: Phase[]): number => {
  const todayISO = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const idx = phases.findIndex((p) => p.endDate >= todayISO);
  return idx;
};

const HOST = {
  name: 'Madrid Talento',
  logo: '/img/sponsors/madrid-talento.png',
};

const SPONSORS: { name: string; logo: string; sizeClass: string }[] = [
  // Cada logo tiene una proporción muy distinta (alto/estrecho, ancho/bajo,
  // cuadrado...), así que cada uno recibe su propia caja ajustada a su
  // forma real, todas escaladas con el mismo factor de aumento, en vez de
  // compartir una única caja genérica que dejaba a unos ilegibles.
  { name: 'Grupo GTT', logo: '/img/sponsors/gtt.png', sizeClass: 'landing-sponsor-logo-gtt' },
  { name: 'Tendios', logo: '/img/sponsors/tendios.png', sizeClass: 'landing-sponsor-logo-tendios' },
  { name: 'Nutco', logo: '/img/sponsors/nutco.png', sizeClass: 'landing-sponsor-logo-nutco' },
];

const BASES_PDF = '/bases-premios-novagob-2026.pdf';

const Landing: React.FC = () => {
  const currentEdition = 2026;
  const lastEdition = 2025;
  const currentPhaseIndex = getCurrentPhaseIndex(PHASES);

  const [categories, setCategories] = useState<Category[]>([]);
  const [basesOpen, setBasesOpen] = useState(false);
  const [nominationsOpen, setNominationsOpen] = useState(false);
  const [wrappedStats, setWrappedStats] = useState<WrappedStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    supabase
      .schema('premios')
      .from('platform_config')
      .select('nominations_submission_enabled')
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        setNominationsOpen(!!data?.nominations_submission_enabled);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Estadísticas agregadas (mismas que alimentan la página /candidaturas).
  useEffect(() => {
    let cancelled = false;
    supabase
      .schema('premios')
      .rpc('get_public_stats')
      .then(({ data }) => {
        if (cancelled) return;
        setWrappedStats((data as WrappedStats) || null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    supabase
      .schema('premios')
      .from('categories')
      .select('*')
      .then(({ data }) => {
        if (cancelled) return;
        // Orden editorial fijo: nominales primero (1-3), proyecto (4-9),
        // benchmark al final (10). Se resuelve por nombre con un ranking
        // explícito; cualquier nombre no contemplado va al final en orden
        // alfabético como respaldo.
        const CATEGORY_ORDER: string[] = [
          'Persona Innovadora del Año',        // 1
          'Mujer destacada del Sector Público', // 2
          'Nuevo talento público',             // 3
          'Creatividad en la innovación',      // 4
          'Proyecto más transformador',        // 5
          'Eficiencia en las AA.PP.',          // 6
          'Transformación Digital',            // 7
          'Gestión de personas',               // 8
          'Buen Gobierno',                     // 9
          'Benchmarking',                      // 10
        ];
        const rank = (name: string) => {
          const i = CATEGORY_ORDER.indexOf(name);
          return i === -1 ? CATEGORY_ORDER.length + 1 : i;
        };
        // En la landing pública NO se muestran las categorías LATAM —
        // sí se incluyen en el formulario de candidatura, votación,
        // validación, etc. (eso lee la tabla directamente).
        const visible = ((data || []) as Category[]).filter(
          (c) => !/LATAM/i.test(c.name)
        );
        const sorted = [...visible].sort((a, b) => {
          const ra = rank(a.name);
          const rb = rank(b.name);
          if (ra !== rb) return ra - rb;
          return a.name.localeCompare(b.name);
        });
        setCategories(sorted);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="landing-page">
      <SeoMeta
        title="Premios NovaGob Excelencia 2026 · Premios a la Innovación Pública en la Administración Pública"
        description="Premios NovaGob Excelencia 2026: el mayor reconocimiento a la innovación pública y la transformación digital en la Administración Pública de España y Latinoamérica. Récord de participación: más de 22.000 votos de 9.500 personas en más de 30 países."
        path="/"
      />
      <ScrollProgress />

      {/* HERO */}
      <section className="landing-hero">
        <div className="landing-hero-glow" aria-hidden="true" />
        <HeroStars />
        <div className="landing-hero-inner">
          <h1 className="landing-hero-title text-gold-shine">
            PREMIOS NOVAGOB
            <span className="landing-sr-only">
              {' '}
              Excelencia — Premios a la innovación pública y la transformación
              digital en la Administración Pública de España y Latinoamérica
            </span>
          </h1>

          <div className="landing-hero-edition">Edición {currentEdition}</div>

          <p className="landing-hero-tagline">
            Reconociendo la innovación y la transformación en la
            <br className="landing-br-desktop" /> Administración Pública
          </p>

          <div className="landing-hero-ctas">
            {nominationsOpen && (
              <Link
                to="/nominate"
                className="landing-btn landing-btn-primary"
              >
                <Send size={18} style={{ marginRight: 8 }} />
                Presentar candidatura
              </Link>
            )}
            <Link to="/valoracion-jurado" className="landing-btn landing-btn-primary">
              <Trophy size={18} style={{ marginRight: 8 }} />
              Ver finalistas
            </Link>
            <Link to="/finalistas-2026" className="landing-btn landing-btn-primary">
              <Sparkles size={18} style={{ marginRight: 8 }} />
              Los 2 últimos finalistas
            </Link>
          </div>

          <div className="landing-hero-ctas-secondary">
            <Link to="/finalistas-2026" className="landing-btn landing-btn-ghost">
              <Camera size={18} style={{ marginRight: 8 }} />
              ¿Eres finalista?
            </Link>
          </div>

          <div className="landing-hero-scrolldown" aria-hidden="true" />
        </div>
      </section>

      {/* BIENVENIDA */}
      <section id="bienvenida" className="landing-section landing-welcome">
        <div className="landing-container">
          <img
            src="/premios-logo.svg"
            alt="Premios NovaGob"
            className="landing-welcome-logo"
          />
          <h2 className="landing-section-title text-gold-shine">Bienvenidos/as</h2>
          <p className="landing-section-lead">
            Los <strong>Premios NovaGob Excelencia</strong> son el mayor
            reconocimiento iberoamericano a las iniciativas, proyectos y personas
            que están transformando la Administración Pública. Cada año, la
            comunidad NovaGob celebra las ideas que marcan la diferencia,
            impulsan la innovación y sitúan a la ciudadanía en el centro.
          </p>
        </div>
      </section>

      {/* ANFITRIÓN (izquierda) Y PATROCINADORES (derecha), misma línea */}
      <section id="anfitrion" className="landing-host-sponsors">
        <div className="landing-container landing-host-sponsors-row">
          <div className="landing-host-sponsors-col">
            <span className="landing-host-label">Anfitrión</span>
            <img
              src={HOST.logo}
              alt={HOST.name}
              className="landing-host-logo"
            />
          </div>

          <div className="landing-host-sponsors-divider" aria-hidden="true" />

          <div id="patrocinadores" className="landing-host-sponsors-col">
            <span className="landing-host-label">Patrocinadores</span>
            <div className="landing-sponsors-row">
              {SPONSORS.map((s) => (
                <img
                  key={s.name}
                  src={s.logo}
                  alt={s.name}
                  className={`landing-sponsor-logo ${s.sizeClass}`}
                />
              ))}
            </div>
          </div>

          <div className="landing-host-sponsors-divider" aria-hidden="true" />

          <div className="landing-host-sponsors-col">
            <span className="landing-host-label">Colaborador</span>
            <img
              src="/img/sponsors/ajuntament-mollet.png"
              alt="Ajuntament de Mollet del Vallès"
              className="landing-host-logo"
            />
          </div>
        </div>
      </section>

      {/* STATS */}
      <StatsBand />

      {/* RESUMEN PÚBLICO DE CANDIDATURAS + CIFRAS RÉCORD DE PARTICIPACIÓN ("wrapped") */}
      <section id="resumen" className="landing-section landing-resumen">
        <div className="landing-container">
          <div className="landing-cta-card landing-resumen-card">
            <div className="landing-cta-card-badge">
              <Globe2 size={14} />
              <span>Resumen de la edición 2026</span>
            </div>

            <h2 className="landing-cta-card-title">
              Cifras <span className="text-gold-shine">récord</span> de participación
            </h2>

            <p className="landing-cta-card-lead">
              De dónde vienen, quién las impulsa y cuántas personas han votado a sus finalistas
              favoritos: un recorrido visual por la edición con más participación de la historia de
              los Premios NovaGob.
            </p>

            <ParticipationStatsBand candidaturas={wrappedStats?.totals.received} />

            <div className="landing-cta-buttons">
              <Link to="/candidaturas" className="landing-btn landing-btn-primary">
                <Globe2 size={18} style={{ marginRight: 8 }} />
                Ver resumen de candidaturas
                <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </Link>
              <Link to="/valoracion-jurado" className="landing-btn landing-btn-primary">
                <Star size={18} style={{ marginRight: 8 }} />
                Ver votaciones del jurado
                <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORÍAS */}
      <section id="categorias" className="landing-section landing-categories">
        <div className="landing-container">
          <div className="landing-categories-header">
            <h2 className="landing-section-title text-gold-shine">
              Categorías
            </h2>
            <p className="landing-section-lead">
              Los Premios NovaGob reconocen la excelencia en distintos ámbitos
              de la innovación pública. Pulsa en cada categoría para leer su
              descripción.
            </p>
          </div>

          <div className="landing-categories-grid">
            {categories.map((cat, idx) => (
              <article key={cat.id} className="landing-category-card">
                <span
                  className="landing-category-card-bgnumber"
                  aria-hidden="true"
                >
                  {String(idx + 1).padStart(2, '0')}
                </span>

                <div className="landing-category-card-top">
                  <span className="landing-category-card-number">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className="landing-category-card-divider" aria-hidden="true" />
                </div>

                <h4 className="landing-category-card-name">{cat.name}</h4>

                <p className="landing-category-card-description">
                  {cat.description || 'Descripción próximamente.'}
                </p>
              </article>
            ))}
          </div>

          {categories.length === 0 && (
            <p
              style={{
                textAlign: 'center',
                color: 'var(--color-text-secondary)',
                marginTop: 24,
              }}
            >
              Cargando categorías…
            </p>
          )}
        </div>
      </section>

      {/* BASES REGULADORAS */}
      <section id="bases" className="landing-section landing-bases">
        <div className="landing-container">
          <div className="landing-bases-inner">
          {/* Mini-portada estilizada del PDF */}
          <div className="landing-bases-cover" aria-hidden="true">
            <div className="landing-bases-cover-page">
              <span className="landing-bases-cover-kicker">Bases</span>
              <span className="landing-bases-cover-title">
                NovaGob
                <br />
                2026
              </span>
              <span className="landing-bases-cover-seal">
                <FileText size={14} />
                PDF
              </span>
            </div>
          </div>
          <div className="landing-bases-body">
            <h2 className="landing-section-title text-gold-shine">
              Bases reguladoras 2026
            </h2>
            <p className="landing-section-lead">
              Consulta el documento oficial completo con requisitos, plazos,
              categorías y criterios de valoración. Puedes leerlo online o
              descargarlo en PDF.
            </p>
            <div className="landing-bases-ctas">
              <button
                type="button"
                className="landing-btn landing-btn-primary"
                onClick={() => setBasesOpen(true)}
              >
                <FileText size={18} style={{ marginRight: 8 }} />
                Leer online
              </button>
              <a
                href={BASES_PDF}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-btn landing-btn-ghost"
                download
              >
                <ArrowRight size={18} style={{ marginRight: 8 }} />
                Descargar PDF
              </a>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* ACTUALIDAD / TIMELINE */}
      <section id="actualidad" className="landing-section landing-actualidad">
        <div className="landing-container">
          <h2 className="landing-section-title text-gold-shine">
            Actualidad: Fases de la Edición {currentEdition}
          </h2>

          <div className="landing-timeline">
            <div className="landing-timeline-line" aria-hidden="true" />

            {PHASES.map((p, idx) => {
              const isCurrent = idx === currentPhaseIndex;

              // Render del botón según su estado
              let ctaNode: React.ReactNode;
              if (p.cta.disabled) {
                ctaNode = (
                  <button
                    className={
                      isCurrent
                        ? 'btn-timeline-highlight'
                        : 'btn-timeline-disabled'
                    }
                    disabled={!isCurrent}
                  >
                    {p.cta.label}
                  </button>
                );
              } else if (p.cta.action === 'open-bases') {
                ctaNode = (
                  <button
                    type="button"
                    className={
                      isCurrent ? 'btn-timeline-highlight' : 'btn-timeline'
                    }
                    onClick={() => setBasesOpen(true)}
                  >
                    {p.cta.label}
                  </button>
                );
              } else if (p.cta.action === 'present-candidatura') {
                // Lleva a /nominate; si el usuario no está logueado,
                // App.tsx redirige a /auth para registrarse / iniciar sesión.
                ctaNode = (
                  <Link
                    to="/nominate"
                    className={
                      isCurrent ? 'btn-timeline-highlight' : 'btn-timeline'
                    }
                  >
                    {p.cta.label}
                  </Link>
                );
              } else if (p.cta.action === 'view-candidaturas') {
                // Enlace al resumen público de candidaturas (/candidaturas).
                ctaNode = (
                  <Link
                    to="/candidaturas"
                    className={
                      isCurrent ? 'btn-timeline-highlight' : 'btn-timeline'
                    }
                  >
                    {p.cta.label}
                  </Link>
                );
              } else if (p.cta.action === 'vote') {
                // Lleva a /votar; si el usuario no está logueado,
                // App.tsx redirige a /auth para registrarse / iniciar sesión.
                ctaNode = (
                  <Link
                    to="/votar"
                    className={
                      isCurrent ? 'btn-timeline-highlight' : 'btn-timeline'
                    }
                  >
                    {p.cta.label}
                  </Link>
                );
              } else if (p.cta.action === 'jury-evaluation') {
                // Página pública con las puntuaciones y comentarios del jurado.
                ctaNode = (
                  <Link
                    to="/valoracion-jurado"
                    className={
                      isCurrent ? 'btn-timeline-highlight' : 'btn-timeline'
                    }
                  >
                    {p.cta.label}
                  </Link>
                );
              } else if (p.cta.action === 'final-two') {
                // Anuncio de los 2 últimos finalistas por categoría, sin revelar orden.
                ctaNode = (
                  <Link
                    to="/finalistas-2026"
                    className={
                      isCurrent ? 'btn-timeline-highlight' : 'btn-timeline'
                    }
                  >
                    {p.cta.label}
                  </Link>
                );
              } else {
                ctaNode = (
                  <a
                    href={p.cta.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={
                      isCurrent ? 'btn-timeline-highlight' : 'btn-timeline'
                    }
                  >
                    {p.cta.label}
                  </a>
                );
              }

              return (
                <div
                  key={idx}
                  className={`landing-timeline-item${
                    isCurrent ? ' current' : ''
                  }`}
                >
                  <div className="landing-timeline-left">
                    <p className="landing-timeline-dates">{p.dates}</p>
                    <h4 className="landing-timeline-title">{p.title}</h4>
                  </div>

                  <div
                    className={`landing-timeline-dot${
                      isCurrent ? ' landing-timeline-dot-gold' : ''
                    }`}
                  >
                    <CheckCircle2 size={18} />
                  </div>

                  <div className="landing-timeline-right">
                    {p.title === 'Gala entrega premios' && (
                      <Countdown target={GALA_DATETIME} doneLabel="¡La gala ya ha empezado!" />
                    )}
                    {ctaNode}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMITÉ Y JURADO */}
      <CommitteeJurySection />

      {/* GANADORES */}
      <section id="ganadores" className="landing-section landing-winners">
        <div className="landing-container">
          <div className="landing-winners-header">
            <h2 className="landing-section-title text-gold-shine">
              Ganadores/as {lastEdition}
            </h2>
            <p className="landing-section-lead">
              Las personas y proyectos que han marcado la excelencia en esta
              edición.
            </p>
          </div>

          <div className="landing-winners-grid">
            {WINNERS_2025.map((w) => (
              <article key={w.category} className="landing-winner-card">
                <Star
                  className="landing-winner-bgstar"
                  size={300}
                  fill="currentColor"
                  strokeWidth={0}
                  aria-hidden="true"
                />
                <Star
                  size={36}
                  className="landing-winner-star"
                  fill="#FFD702"
                  strokeWidth={1}
                />
                <h4 className="landing-winner-category">{w.category}</h4>
                <p className="landing-winner-name">{w.winner}</p>
                {w.extra && (
                  <p className="landing-winner-extra">{w.extra}</p>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <TestimonialsSection />

      {/* REVIVE LA EDICIÓN 2025 (carrusel + vídeo) */}
      <ReviveSection />

      {/* EDICIONES ANTERIORES */}
      <PastEditionsSection />

      <LandingFooter />

      <BasesModal open={basesOpen} onClose={() => setBasesOpen(false)} />
    </div>
  );
};

export default Landing;
